import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import SettingsIcon from '@mui/icons-material/Settings'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LastUpdatedBadge from './LastUpdatedBadge'
import { markRefreshed } from 'app/slice/executive.slice'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { selectMe } from 'app/slice/user.slice'
import { canManageTargets } from 'features/executive/permission-store'
import { useHistory } from 'utils/router-compat'
import { routeName } from 'routes/routes-name'
import { useExportPdf } from 'features/executive/export/use-export-pdf'
import type { ExecutiveTab } from 'api/executive/executive.types'
import { colors } from 'constants/theme'

interface TabHeaderProps {
  activeTab: string  // localized label
}

const pathToTab = (pathname: string): ExecutiveTab => {
  if (pathname.startsWith(routeName.executiveAccount)) return 'account'
  if (pathname.startsWith(routeName.executiveSales)) return 'sales'
  if (pathname.startsWith(routeName.executiveInsight)) return 'insight'
  return 'performance'
}

export default function TabHeader({ activeTab }: TabHeaderProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const canManage = canManageTargets()

  const tab = pathToTab(location.pathname)
  const { exportTab } = useExportPdf()
  const anyLoading = useSelector((s: { executive: ExecutiveState }) =>
    Object.keys(s.executive.loadingWidgets ?? {}).length > 0
  )

  const handleRefresh = () => {
    dispatch(markRefreshed())
  }
  const handleExportPdf = () => {
    if (!anyLoading) exportTab(tab)
  }

  const exportTooltip = anyLoading
    ? t('EXECUTIVE.HEADER.EXPORT_PDF_DISABLED')
    : t('EXECUTIVE.HEADER.EXPORT_PDF_TOOLTIP')

  return (
    <Box
      className="hideInPrint"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // Wrap so the title block + toolbar stack vertically on mobile rather
        // than colliding. Horizontal padding tightens on xs.
        flexWrap: 'wrap',
        gap: 1.5,
        px: { xs: 2, sm: 3 },
        py: 1.5,
        minHeight: 64,
        // Navy bottom border anchors the page header — pairs with the
        // SectionHeader navy left-bar to give the page a consistent
        // "executive report" frame.
        borderBottom: `2px solid ${colors.themeMainColor}`,
      }}
    >
      {/* Page title block — breadcrumb sits small above the active tab name,
          which becomes the dominant heading the executive scans first. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: colors.textLightGray, lineHeight: 1.2 }}>
          {t('EXECUTIVE.BREADCRUMB')}
        </Typography>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 600,
            color: colors.themeMainColor,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {activeTab}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <LastUpdatedBadge />
        {/* Icon cluster grouped in a single bordered chip so the actions
            read as one toolbar rather than three loose buttons. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${colors.lightGray}`,
            borderRadius: 1.5,
            overflow: 'hidden',
            '& .MuiIconButton-root': { borderRadius: 0, color: colors.themeMainColor },
            '& .MuiIconButton-root:not(:last-of-type)': {
              borderRight: `1px solid ${colors.lightGray}`,
            },
          }}
        >
          <Tooltip title={t('EXECUTIVE.HEADER.REFRESH_TOOLTIP') || ''}>
            <IconButton size="small" onClick={handleRefresh} aria-label="refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
            <Tooltip title={t('EXECUTIVE.HEADER.TARGET_SETUP_TOOLTIP') || ''}>
              <IconButton
                size="small"
                onClick={() => history.push(routeName.dashboardTargets)}
                aria-label="dashboard target settings"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={exportTooltip}>
            <span>
              <IconButton
                size="small"
                onClick={handleExportPdf}
                disabled={anyLoading}
                aria-label="export pdf"
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )
}
